import { QueryClient, useQuery, useQueryClient } from 'react-query';
import api from 'services';
import type { ApiError } from 'services/api.errors';
import { checkCondition } from 'services/api.errors';
import type { ID, IraUser, IraUserResponse, IraUsersResponse } from 'services/requests';
import { useQueryParams } from 'hooks/routes';
import { findUserInTableItems } from 'utils/findUsersInCachedQueries';
import { mapCustodianTypeKeyToValue, mapIraUserFields } from 'utils';
import { Parameters } from 'constants/queryParameters';
import { QueryKeys } from 'constants/queryKeys';
import type { AxiosError } from 'axios';

export const useGetIraUser = () => {
  const queryClient = useQueryClient();
  const id = useQueryParams(Parameters.iraUserId);
  const queryKey = [QueryKeys.iraUser, id];

  return useQuery<IraUserResponse, AxiosError<ApiError, string>>(
    queryKey,
    queryFn(id, queryKey),
    {
      enabled: Boolean(id),
      initialData: getIraUserFromCache(queryClient, id),
      initialDataUpdatedAt: getUpdateTimeIraUsers(queryClient),
      refetchOnWindowFocus: false,
    },
  );
};

export const useGetIraUserPrefetch = () => {
  const queryClient = useQueryClient();

  return async (id: ID) => {
    const queryKey = [QueryKeys.iraUser, id];

    await queryClient.prefetchQuery(queryKey, queryFn(id, queryKey), {
      staleTime: 1000,
      initialData: getIraUserFromCache(queryClient, id),
      initialDataUpdatedAt: getUpdateTimeIraUsers(queryClient),
    });
  };
};

function getIraUserFromCache(queryClient: QueryClient, id: ID | undefined) {
  return () => {
    if (id === undefined) return;

    const cachedQueries = queryClient.getQueriesData<IraUsersResponse>({
      queryKey: [QueryKeys.iraUsers],
    });

    const user = findUserInTableItems<IraUser, IraUsersResponse>(cachedQueries, id);

    if (user) {
      return {
        ...user,
        custodianType: mapCustodianTypeKeyToValue(user.custodianType),
      };
    }

    return;
  };
}

function getUpdateTimeIraUsers(queryClient: QueryClient) {
  return () => queryClient.getQueryState(QueryKeys.iraUsers)?.dataUpdatedAt;
}

function queryFn(id: ID | undefined, queryKey?: string | unknown[]) {
  return async () => {
    checkCondition(id, queryKey);
    const { data } = await api.getIraUser(id);
    const modifiedIraUserData = mapIraUserFields(data);
    return modifiedIraUserData;
  };
}
